import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface SwipeOptions {
  minSwipeDistance?: number;
  enableBackNavigation?: boolean;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
}

interface TouchPosition {
  x: number;
  y: number;
}

export const useSwipeNavigation = ({
  minSwipeDistance = 50,
  enableBackNavigation = true,
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
}: SwipeOptions = {}) => {
  const navigate = useNavigate();
  const touchStart = useRef<TouchPosition | null>(null);
  const touchEnd = useRef<TouchPosition | null>(null);

  // Set up the listeners for touch events
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStart.current = {
        x: e.targetTouches[0].clientX,
        y: e.targetTouches[0].clientY,
      };
    };

    const handleTouchMove = (e: TouchEvent) => {
      touchEnd.current = {
        x: e.targetTouches[0].clientX,
        y: e.targetTouches[0].clientY,
      };
    };

    const handleTouchEnd = () => {
      if (!touchStart.current || !touchEnd.current) return;
      
      const distanceX = touchEnd.current.x - touchStart.current.x;
      const distanceY = touchEnd.current.y - touchStart.current.y;
      const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
      
      // Horizontal swipe detection
      if (isHorizontalSwipe) {
        if (distanceX > minSwipeDistance) {
          // Swipe right
          if (enableBackNavigation) {
            navigate(-1);
          }
          onSwipeRight?.();
        } else if (distanceX < -minSwipeDistance) {
          // Swipe left
          onSwipeLeft?.();
        }
      } 
      // Vertical swipe detection
      else {
        if (distanceY > minSwipeDistance) {
          // Swipe down
          onSwipeDown?.();
        } else if (distanceY < -minSwipeDistance) {
          // Swipe up
          onSwipeUp?.();
        }
      }
      
      // Reset
      touchStart.current = null;
      touchEnd.current = null;
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    // Clean up
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [navigate, minSwipeDistance, enableBackNavigation, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown]);

  return {
    enableSwipeNavigation: (enabled: boolean) => {
      return enabled;
    }
  };
};

export default useSwipeNavigation; 